/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Asset Detail Inspector Drawer Component (Task 10.4.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  X,
  Heart,
  Download,
  Copy,
  Check,
  Sparkles,
  Database,
  Trash2,
  Edit2,
  ExternalLink,
  Volume2,
  Film,
  Image as ImageIcon,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { MediaAsset, formatBytes } from '../../stores/useMediaGalleryStore';
import { CustomVideoPlayer, PlayableVideoData } from '../media/CustomVideoPlayer';

interface AssetDetailDrawerProps {
  asset: MediaAsset | null;
  onClose: () => void;
  onToggleFavorite: (id: string) => void;
  onRename: (id: string, newTitle: string) => void;
  onDelete: (id: string) => void;
  onReusePrompt: (asset: MediaAsset) => void;
}

export const AssetDetailDrawer: React.FC<AssetDetailDrawerProps> = ({
  asset,
  onClose,
  onToggleFavorite,
  onRename,
  onDelete,
  onReusePrompt,
}) => {
  const { t } = useTranslation();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!asset) return null;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleStartRename = () => {
    setEditedTitle(asset.title);
    setIsEditingTitle(true);
  };

  const handleSaveRename = () => {
    if (editedTitle.trim()) {
      onRename(asset.id, editedTitle.trim());
    }
    setIsEditingTitle(false);
  };

  const playableVideoData: PlayableVideoData = {
    id: asset.id,
    videoUrl: asset.url,
    thumbnailUrl: asset.thumbnailUrl,
    durationSeconds: asset.durationSeconds ?? 5,
    aspectRatio: (asset.aspectRatio === '9:16' || asset.aspectRatio === '1:1' ? asset.aspectRatio : '16:9'),
    model: asset.generatorName,
    loop: true,
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-lg lg:max-w-xl bg-surface border-l border-surface-border shadow-2xl flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-surface-border flex items-center justify-between bg-surface-elevated/50 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                {asset.assetType === 'image' && <ImageIcon className="w-4 h-4" />}
                {asset.assetType === 'video' && <Film className="w-4 h-4" />}
                {asset.assetType === 'audio' && <Volume2 className="w-4 h-4" />}
              </span>
              <div>
                <h3 className="text-sm font-heading font-bold text-foreground">
                  {t('gallery.assetDetails')}
                </h3>
                <p className="text-[11px] text-foreground-muted">
                  {t('gallery.assetDetailsDesc')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onToggleFavorite(asset.id)}
                className={`p-2 rounded-lg border transition-colors ${
                  asset.isFavorite
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-500'
                    : 'border-surface-border text-foreground-muted hover:text-foreground hover:bg-surface-elevated'
                }`}
                title={asset.isFavorite ? t('gallery.unfavoriteAsset') : t('gallery.favoriteAsset')}
              >
                <Heart className={`w-4 h-4 ${asset.isFavorite ? 'fill-current' : ''}`} />
              </button>

              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-lg border border-surface-border text-foreground-muted hover:text-foreground hover:bg-surface-elevated transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Scrollable Content Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* 1. Media Preview Area */}
            <div className="rounded-2xl border border-surface-border overflow-hidden bg-black/40 relative shadow-glass">
              {asset.assetType === 'video' ? (
                <div className="w-full">
                  <CustomVideoPlayer video={playableVideoData} />
                </div>
              ) : asset.assetType === 'audio' ? (
                <div className="p-8 flex flex-col items-center justify-center space-y-4 bg-gradient-to-br from-primary/15 via-surface-elevated to-accent/15">
                  <div className="w-20 h-20 rounded-2xl bg-primary/20 text-primary flex items-center justify-center shadow-glass">
                    <Volume2 className="w-10 h-10 animate-pulse" />
                  </div>
                  <div className="text-center">
                    <h4 className="text-sm font-semibold text-foreground">{asset.title}</h4>
                    <p className="text-xs text-foreground-muted mt-0.5 font-mono">
                      {asset.durationSeconds}s • {asset.bitrate} • {asset.mimeType}
                    </p>
                  </div>
                  <audio
                    controls
                    src={asset.url}
                    className="w-full max-w-sm rounded-lg"
                  />
                </div>
              ) : (
                <div className="relative group max-h-[420px] flex items-center justify-center bg-surface-elevated">
                  <img
                    src={asset.url}
                    alt={asset.title}
                    className="w-full max-h-[420px] object-contain"
                  />
                  <a
                    href={asset.url}
                    target="_blank"
                    rel="noreferrer"
                    className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-black/70 backdrop-blur-md text-white text-xs font-medium flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity border border-white/20"
                  >
                    <span>Full HD</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </div>

            {/* 2. Asset Title & Renaming */}
            <div className="p-4 rounded-xl border border-surface-border bg-surface-elevated/40 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                  {asset.generatorName}
                </span>
                {!isEditingTitle && (
                  <button
                    type="button"
                    onClick={handleStartRename}
                    className="text-xs text-foreground-muted hover:text-primary flex items-center gap-1 transition-colors"
                  >
                    <Edit2 className="w-3 h-3" />
                    <span>{t('gallery.renameAsset')}</span>
                  </button>
                )}
              </div>

              {isEditingTitle ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveRename()}
                    placeholder={t('gallery.editTitlePlaceholder')}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-primary bg-surface text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleSaveRename}
                    className="px-3 py-1.5 rounded-lg bg-primary text-primary-fg text-xs font-semibold shadow-sm"
                  >
                    {t('gallery.saveTitle')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditingTitle(false)}
                    className="px-2.5 py-1.5 rounded-lg border border-surface-border text-foreground-muted text-xs hover:bg-surface"
                  >
                    {t('gallery.cancelSelect')}
                  </button>
                </div>
              ) : (
                <h2 className="text-base font-bold text-foreground tracking-tight">
                  {asset.title}
                </h2>
              )}
            </div>

            {/* 3. Action Buttons (Reuse Prompt & Download) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onReusePrompt(asset)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-fg font-semibold text-xs shadow-glow-primary/20 hover:opacity-95 transition-all"
              >
                <Sparkles className="w-4 h-4" />
                <span>{t('gallery.reusePrompt')}</span>
              </button>

              <a
                href={asset.url}
                download={asset.title}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-surface-border text-foreground font-semibold text-xs transition-colors"
              >
                <Download className="w-4 h-4 text-emerald-500" />
                <span>{t('gallery.downloadAsset')}</span>
              </a>
            </div>

            {/* 4. Prompt Lineage Section */}
            {asset.prompt && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-muted">
                    {t('gallery.promptTitle')}
                  </h4>
                  <button
                    type="button"
                    onClick={() => handleCopy(asset.prompt!, 'prompt')}
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                  >
                    {copiedField === 'prompt' ? (
                      <>
                        <Check className="w-3 h-3 text-emerald-500" />
                        <span className="text-emerald-500">{t('gallery.promptCopied')}</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span>{t('gallery.copyPrompt')}</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="p-3.5 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono text-foreground leading-relaxed">
                  {asset.prompt}
                </div>
              </div>
            )}

            {/* Negative Prompt */}
            {asset.negativePrompt && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-muted">
                  {t('gallery.negativePromptTitle')}
                </h4>
                <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated/60 text-xs font-mono text-foreground-muted leading-relaxed">
                  {asset.negativePrompt}
                </div>
              </div>
            )}

            {/* 5. File Specifications Table */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-muted">
                {t('gallery.fileInfo')}
              </h4>

              <div className="rounded-xl border border-surface-border divide-y divide-surface-border overflow-hidden bg-surface-elevated/40 text-xs">
                {asset.width && asset.height && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-foreground-muted">{t('gallery.dimensions')}</span>
                    <span className="font-mono font-medium text-foreground">
                      {asset.width} × {asset.height} px {asset.aspectRatio && `(${asset.aspectRatio})`}
                    </span>
                  </div>
                )}

                <div className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-foreground-muted">{t('gallery.fileSize')}</span>
                  <span className="font-mono font-medium text-foreground">
                    {formatBytes(asset.fileSizeBytes)} ({asset.fileSizeBytes.toLocaleString()} bytes)
                  </span>
                </div>

                <div className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-foreground-muted">{t('gallery.mimeType')}</span>
                  <span className="font-mono text-primary font-medium">{asset.mimeType}</span>
                </div>

                {asset.durationSeconds && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-foreground-muted">{t('gallery.duration')}</span>
                    <span className="font-mono font-medium text-foreground">
                      {asset.durationSeconds} seconds
                    </span>
                  </div>
                )}

                {asset.bitrate && (
                  <div className="px-4 py-2.5 flex items-center justify-between">
                    <span className="text-foreground-muted">{t('gallery.bitrate')}</span>
                    <span className="font-mono font-medium text-foreground">{asset.bitrate}</span>
                  </div>
                )}

                <div className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-foreground-muted">{t('gallery.generatorModel')}</span>
                  <span className="font-medium text-foreground">{asset.generatorName}</span>
                </div>

                <div className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-foreground-muted">{t('gallery.creditsConsumed')}</span>
                  <span className="font-mono font-semibold text-primary">{asset.creditsCost} Credits</span>
                </div>

                <div className="px-4 py-2.5 flex items-center justify-between">
                  <span className="text-foreground-muted">{t('gallery.createdAt')}</span>
                  <span className="font-mono text-foreground-muted">
                    {new Date(asset.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* 6. Cloudflare R2 Storage Lineage */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-muted flex items-center gap-1.5">
                <Database className="w-3.5 h-3.5 text-primary" />
                <span>{t('gallery.storageDetails')}</span>
              </h4>

              <div className="p-3.5 rounded-xl border border-surface-border bg-surface-elevated/40 space-y-2.5 text-xs">
                <div>
                  <span className="text-[10px] uppercase font-bold text-foreground-muted">
                    {t('gallery.r2Key')}
                  </span>
                  <p className="font-mono text-xs text-foreground break-all mt-0.5">
                    {asset.r2Key}
                  </p>
                </div>

                <div className="pt-2 border-t border-surface-border flex items-center justify-between gap-2">
                  <div className="truncate">
                    <span className="text-[10px] uppercase font-bold text-foreground-muted block">
                      {t('gallery.cdnUrl')}
                    </span>
                    <span className="font-mono text-[11px] text-foreground-muted truncate block">
                      {asset.url}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleCopy(asset.url, 'cdnUrl')}
                    className="px-2.5 py-1.5 rounded-lg border border-surface-border bg-surface text-foreground hover:bg-surface-elevated text-xs font-medium flex items-center gap-1 flex-shrink-0 transition-colors"
                  >
                    {copiedField === 'cdnUrl' ? (
                      <Check className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                    <span>{copiedField === 'cdnUrl' ? t('gallery.cdnUrlCopied') : t('gallery.copyCdnUrl')}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* 7. Danger Zone: Delete Asset */}
            <div className="pt-4 border-t border-surface-border">
              {showDeleteConfirm ? (
                <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 space-y-3">
                  <p className="text-xs text-rose-400">
                    {t('gallery.deleteAssetConfirm', { name: asset.title })}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(asset.id);
                        onClose();
                      }}
                      className="px-3.5 py-1.5 rounded-lg bg-rose-600 text-white text-xs font-bold shadow-sm hover:bg-rose-700 transition-colors"
                    >
                      {t('gallery.deleteAsset')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      className="px-3 py-1.5 rounded-lg border border-surface-border bg-surface text-foreground-muted text-xs hover:bg-surface-elevated"
                    >
                      {t('gallery.cancelSelect')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full py-2.5 rounded-xl border border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{t('gallery.deleteAsset')}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
